import { Fragment, useMemo } from 'react';

import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import './NavbarPanel.sass';
import { buildTestid } from '../../../lib/utils';
import type { NavbarContext } from '../types';
import { NavbarContextProvider, useNavbarContext } from '../useNavbarContext';

export interface NavbarPanelProps extends
  WithDataTestid,
  WithClassNameProp {
  header?: React.ReactElement;
  sections?: (React.ReactElement[]) | ((ctx: NavbarContext) => React.ReactElement[]);
  footer?: React.ReactElement;
}

const displayName = 'NavbarPanel';
const testid = buildTestid(displayName);

function NavbarPanel({
  header = undefined,
  sections = undefined,
  footer = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: NavbarPanelProps): React.JSX.Element {
  const ctx = useNavbarContext();

  const $sections = useMemo(() => {
    if (Array.isArray(sections)) return sections;
    if (typeof sections === 'function') return sections(ctx);
    return [];
  }, [sections, ctx]);

  return (
    <div
      {...testid(dataTestId)}
      className={cn('NavbarPanel', {
        [`NavbarPanel_${ctx.type}`]: ctx.type,
        NavbarPanel_withFooter: footer,
      }, className)}
    >
      {header}

      <div
        className={cn('NavbarPanel__Sections')}
        {...testid('Sections', dataTestId)}
      >
        {$sections.map((section, sectionIdx) => (
          <Fragment key={section.key}>
            {sectionIdx > 0 && <div className={cn('NavbarPanel__SectionsDivider')} />}
            {section}
          </Fragment>
        ))}
      </div>

      {footer}
    </div>
  );
}

NavbarPanel.displayName = displayName;

// eslint-disable-next-line react/require-default-props
function NavbarPanelWithProvider(props: NavbarPanelProps): React.JSX.Element {
  return (
    <NavbarContextProvider>
      <NavbarPanel {...props} />
    </NavbarContextProvider>
  );
}

export { NavbarPanelWithProvider as NavbarPanel };
